import { create } from 'zustand';
import { api } from '@/api/client';
import { startSession, endSession, track } from '@/state/telemetry';
import type { Role, User, LoginRequest, MeResponse } from '@/types/api';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  /** Активная роль может отличаться от default_role (role-switcher). */
  activeRole: Role | null;
  bootstrap: () => Promise<void>;
  login: (creds: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  switchRole: (role: Role) => void;
  /** Обновить поля user в сторе (после смены имени и т.п.). */
  updateUser: (patch: Partial<User>) => void;
}

// Переключённая роль переживает перезагрузку страницы (2026-08-26). Раньше
// activeRole жила только в памяти и на F5 сбрасывалась в default_role — режим
// «смотреть кабинет глазами другой роли» разваливался при любом обновлении.
const ROLE_KEY = 'collegium_active_role';
const VALID_ROLES: Role[] = ['curator', 'expert', 'gip', 'admin', 'customer'];

function readSavedRole(defaultRole: Role): Role {
  try {
    const saved = localStorage.getItem(ROLE_KEY) as Role | null;
    if (!saved || !VALID_ROLES.includes(saved)) return defaultRole;
    // Чужую роль подставлять можно только админу — иначе localStorage стал бы
    // способом «повысить» себя в интерфейсе (бэкенд бы всё равно отказал).
    if (defaultRole !== 'admin' && saved !== defaultRole) return defaultRole;
    return saved;
  } catch {
    return defaultRole;
  }
}

function writeSavedRole(role: Role | null): void {
  try {
    if (role) localStorage.setItem(ROLE_KEY, role);
    else localStorage.removeItem(ROLE_KEY);
  } catch { /* приватный режим / отключённое хранилище — не критично */ }
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  error: null,
  activeRole: null,

  /** Вызывается при старте SPA — проверяет существующую сессию через /auth/me. */
  bootstrap: async () => {
    set({ loading: true, error: null });
    try {
      const me = await api.get<MeResponse>('/auth/me');
      const restored = readSavedRole(me.user.default_role);
      set({ user: me.user, activeRole: restored, loading: false });
      // Стартуем UI-сессию для telemetry
      startSession(restored);
    } catch {
      // 401 = нет сессии. Не считаем ошибкой — пользователь просто не залогинен.
      set({ user: null, activeRole: null, loading: false });
    }
  },

  login: async (creds) => {
    set({ loading: true, error: null });
    try {
      const res = await api.post<MeResponse>('/auth/login', creds);
      set({ user: res.user, activeRole: res.user.default_role, loading: false });
      startSession(res.user.default_role);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Не удалось войти';
      set({ loading: false, error: msg });
      throw e;
    }
  },

  logout: async () => {
    track('logout_initiated');
    await endSession('logout');
    try {
      await api.post('/auth/logout');
    } finally {
      writeSavedRole(null);
      set({ user: null, activeRole: null });
    }
  },

  switchRole: (role) => {
    const u = get().user;
    if (!u) return;
    // Admin может переключаться на любую роль. Остальные — только на свою default.
    if (u.default_role !== 'admin' && role !== u.default_role) return;
    const prev = get().activeRole;
    set({ activeRole: role });
    writeSavedRole(role);
    track('role_switched', { payload: { from: prev, to: role } });
    // Перезапуск сессии под новой ролью
    startSession(role);
  },

  updateUser: (patch) => {
    const u = get().user;
    if (!u) return;
    set({ user: { ...u, ...patch } });
  },
}));
